import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { currencySymbol } from "../../utils/currencyUtils";

/**
 * EditGroupModal
 *
 * Modal para editar las opciones de un grupo:
 * - Nombre del grupo (solo el líder)
 * - Presupuesto personal para el grupo
 * - Qué wishlist mostrar en el grupo
 * - Ícono del grupo
 * - Eliminar grupo (solo líder) o abandonarlo (miembros)
 */
export default function EditGroupModal({
  user, userProfile, setUserProfile,
  lang, darkMode,
  currentFamily, userFamilies,
  popupFamily, setCurrentFamily, setUserFamilies, setMembers,
  groupIcons,
  editNameValue, setEditNameValue,
  editNameOpenedFrom,
  editingGroupIcon, setEditingGroupIcon,
  budgetInput, setBudgetInput,
  wishlistNames, loadWishlistNames,
  showToast, tr,
  setShowEditName,
  setActiveTab,
  saveGroupIcon,
}) {
  const pf = popupFamily || currentFamily;
  const pfFull = (pf && !pf.leaderId)
    ? (userFamilies.find((f) => f.id === pf.id) || pf)
    : pf;
  const isAdmin = pfFull && user && pfFull.leaderId === user.uid;

  const cerrar = () => {
    setShowEditName(false);
    setEditingGroupIcon("");
    if (editNameOpenedFrom !== "groups") setActiveTab("profile");
  };

  const guardarYCerrar = async () => {
    // Guardar ícono si cambió
    if (editingGroupIcon) {
      saveGroupIcon(pf.id, editingGroupIcon);
      setEditingGroupIcon("");
    }

    // Guardar presupuesto si se introdujo uno
    if (user && pf && budgetInput !== "") {
      const val = parseFloat(budgetInput) || 0;
      const budgetCurrency = userProfile?.currency || "USD";
      await updateDoc(doc(db, "users", user.uid), {
        [`groupBudgets.${pf.id}`]: val,
        [`groupBudgetCurrencies.${pf.id}`]: budgetCurrency,
      });
      setUserProfile((p) => ({
        ...p,
        groupBudgets: { ...(p?.groupBudgets || {}), [pf.id]: val },
        groupBudgetCurrencies: { ...(p?.groupBudgetCurrencies || {}), [pf.id]: budgetCurrency },
      }));
    }

    // Guardar nombre del grupo si cambió (solo admin)
    if (isAdmin && editNameValue.trim() && editNameValue.trim() !== pf.name) {
      await updateDoc(doc(db, "families", pf.id), { name: editNameValue.trim() });
      setCurrentFamily((f) => ({ ...f, name: editNameValue.trim() }));
      setUserFamilies((fs) => fs.map((f) => f.id === pf.id ? { ...f, name: editNameValue.trim() } : f));
      showToast(tr("familySaved"));
    } else if (editingGroupIcon || budgetInput !== "") {
      showToast("✓ " + (lang === "es" ? "Guardado" : lang === "fi" ? "Tallennettu" : "Saved"));
    }

    setShowEditName(false);
    if (editNameOpenedFrom !== "groups") setActiveTab("profile");
  };

  const eliminarGrupo = async () => {
    if (!window.confirm(tr("deleteGroupConfirm"))) return;
    try {
      const fSnap = await getDoc(doc(db, "families", pf.id));
      if (fSnap.exists()) {
        const memberIds = fSnap.data().memberIds || [];
        await Promise.all(memberIds.map((mid) =>
          updateDoc(doc(db, "users", mid), {
            families: (userProfile?.families || []).filter((id) => id !== pf.id),
          })
        ));
        const { deleteDoc } = await import("firebase/firestore");
        await deleteDoc(doc(db, "families", pf.id));
      }
    } catch (e) {
      console.error("deleteGroup cleanup error", e);
    }
    const remaining = userFamilies.filter((f) => f.id !== pf.id);
    setUserFamilies(remaining);
    setUserProfile((p) => ({ ...p, families: (p.families || []).filter((id) => id !== pf.id) }));
    setCurrentFamily(remaining[0] || null);
    setShowEditName(false);
    showToast(tr("deleteGroup"));
  };

  const abandonarGrupo = async () => {
    if (!window.confirm(tr("leaveGroupConfirm"))) return;
    const currentIds = (userProfile?.families || []).filter((id) => id !== pf.id);
    const fSnap = await getDoc(doc(db, "families", pf.id));
    const newMemberIds = fSnap.data().memberIds.filter((id) => id !== user.uid);
    await updateDoc(doc(db, "families", pf.id), { memberIds: newMemberIds });
    await updateDoc(doc(db, "users", user.uid), { families: currentIds });
    setUserProfile((p) => ({ ...p, families: currentIds }));
    setUserFamilies((fs) => fs.filter((f) => f.id !== pf.id));
    setCurrentFamily(userFamilies.filter((f) => f.id !== pf.id)[0] || null);
    setMembers((ms) => ms.filter((m) => m.id !== user.uid));
    setShowEditName(false);
    showToast(tr("leaveGroup"));
  };

  const crearNuevaWishlu = async () => {
    const { generateId } = await import("../../utils/idUtils");
    const { setDoc, arrayUnion } = await import("firebase/firestore");
    const wishlistId = generateId();
    const wName = (userProfile?.name || "My") + "'s list";
    await setDoc(doc(db, "wishlists", wishlistId), { ownerId: user.uid, name: wName, gifts: [] });
    await updateDoc(doc(db, "users", user.uid), {
      wishlists: arrayUnion(wishlistId),
      [`familyWishlists.${pf.id}`]: wishlistId,
    });
    setUserProfile((p) => ({
      ...p,
      wishlists: [...(p?.wishlists || []), wishlistId],
      familyWishlists: { ...(p?.familyWishlists || {}), [pf.id]: wishlistId },
    }));
    showToast(lang === "es" ? "✅ Nueva wishlu creada y asignada" : "✅ New wishlu created");
  };

  const cambiarWishlu = async (wid) => {
    await updateDoc(doc(db, "users", user.uid), { [`familyWishlists.${pf.id}`]: wid });
    setUserProfile((p) => ({
      ...p,
      familyWishlists: { ...(p?.familyWishlists || {}), [pf.id]: wid },
    }));
    showToast(lang === "es" ? "✅ Wishlu cambiada" : lang === "fi" ? "✅ Wishlu vaihdettu" : "✅ Wishlu changed");
  };

  const ICONOS = ["👨‍👩‍👧‍👦","🌙","⭐","🎁","🎄","🏠","💫","🌸","🎉","🦋","🌈","🍀","❤️","🔥","🌻","🐾","🎸","🌴","🦄","🎮","🐀","🍕","✈️"];
  const allWlIds = userProfile?.wishlists || [];
  const currentAssigned = (userProfile?.familyWishlists || {})[pf?.id];

  return (
    <div className="edit-name-popup" onClick={(e) => { if (e.target === e.currentTarget) cerrar(); }}>
      <div className="edit-name-box">

        {/* Cabecera */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h4 style={{ margin: 0 }}>{tr("groupOptions")}</h4>
          <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#aaa", padding: "0 4px", lineHeight: 1 }} onClick={cerrar}>✕</button>
        </div>

        {/* Nombre del grupo (solo admin) */}
        {isAdmin && (
          <div className="campo">
            <label className="campo-label">{tr("editFamilyName")}</label>
            <input className="campo-input" value={editNameValue} autoFocus onChange={(e) => setEditNameValue(e.target.value)} />
          </div>
        )}

        {/* Presupuesto */}
        {user && (
          <div className="campo">
            <label className="campo-label">
              💰 {tr("budgetTitle")}{" "}
              <span style={{ fontSize: 10, color: "#aaa", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                ({currencySymbol(userProfile?.currency || "USD")})
              </span>
            </label>
            <input className="campo-input" placeholder={tr("budgetPlaceholder")} value={budgetInput} inputMode="decimal"
              onChange={(e) => setBudgetInput(e.target.value.replace(/[^0-9.]/g, ""))} />
          </div>
        )}

        {/* Selector de wishlist */}
        {user && pf && (
          <div className="campo">
            <label className="campo-label">
              🎁 {lang === "es" ? "Wishlu en este grupo" : lang === "fi" ? "Wishlu tässä ryhmässä" : "Wishlu in this group"}
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {allWlIds.length === 0 && (
                <p style={{ fontSize: 12, color: "#aaa" }}>{lang === "es" ? "No tienes wishlus" : "No wishlus yet"}</p>
              )}
              {allWlIds.map((wid) => {
                const isSelected = wid === currentAssigned;
                return (
                  <div key={wid}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, cursor: "pointer",
                      border: "1.5px solid " + (isSelected ? "#d4a853" : (darkMode ? "#2e2e2e" : "#e0dbd2")),
                      background: isSelected ? (darkMode ? "#2a2200" : "#fffbf0") : (darkMode ? "#1a1a1a" : "#faf8f5"),
                    }}
                    onClick={() => { if (!isSelected) cambiarWishlu(wid); }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                      border: "2px solid " + (isSelected ? "#d4a853" : "#aaa"),
                      background: isSelected ? "#d4a853" : "transparent" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: darkMode ? "#e8e2d9" : "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {wishlistNames[wid] || "Wishlu " + wid.slice(-4)}
                      </div>
                    </div>
                    {isSelected && <span style={{ fontSize: 12, color: "#d4a853", fontWeight: 700 }}>✓</span>}
                  </div>
                );
              })}
              {/* Crear nueva wishlu */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10,
                border: "1.5px dashed #d4a853", cursor: "pointer", color: "#d4a853", fontSize: 13, fontWeight: 600 }}
                onClick={crearNuevaWishlu}>
                ＋ {lang === "es" ? "Crear nueva wishlu aquí" : lang === "fi" ? "Luo uusi wishlu" : "Create new wishlu here"}
              </div>
            </div>
          </div>
        )}

        {/* Selector de ícono */}
        <div style={{ borderTop: "1px solid #f0ece6", marginTop: 4, paddingTop: 12 }}>
          <p style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>
            {tr("changeGroupIcon")}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {ICONOS.map((emoji) => {
              const sel = (editingGroupIcon ? editingGroupIcon : (groupIcons[pf?.id] || "🌙")) === emoji;
              return (
                <button key={emoji} onClick={() => setEditingGroupIcon(emoji)}
                  style={{ fontSize: 24, background: sel ? "#fff8e6" : "transparent",
                    border: sel ? "2px solid #d4a853" : "2px solid transparent",
                    borderRadius: 10, padding: 4, cursor: "pointer" }}>
                  {emoji}
                </button>
              );
            })}
          </div>
        </div>

        {/* Botones guardar/cancelar */}
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <button className="btn btn-dorado" style={{ flex: 2 }} onClick={guardarYCerrar}>{tr("save")}</button>
          <button className="btn btn-gris" onClick={cerrar}>{tr("cancel")}</button>
        </div>

        {/* Eliminar grupo (admin) o Abandonar (miembro) */}
        {isAdmin && (
          <button style={{ background: "none", border: "none", fontSize: 12, color: "#bbb", cursor: "pointer", padding: "4px 0" }}
            onClick={eliminarGrupo}>
            🗑️ {tr("deleteGroup")}
          </button>
        )}
        {!isAdmin && (
          <button style={{ background: "none", border: "none", fontSize: 12, color: "#c0392b", cursor: "pointer", padding: "4px 0", marginTop: 4 }}
            onClick={abandonarGrupo}>
            🚪 {tr("leaveGroup")}
          </button>
        )}

      </div>
    </div>
  );
}
